import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Check, ArrowLeft, Star, X } from "lucide-react";
import { StarPicker } from "../components/blocks/NewBlocks";
import { apiAddReview } from "../api";
import { FaWhatsapp } from "react-icons/fa";
import { usePublicPortfolio } from "./PortfolioView";

// Fondo minimalista: negro con círculos y líneas flotantes azules y blancos.
const DOTS = Array.from({ length: 18 }, (_, i) => ({
  left: (i * 53) % 100,
  size: 4 + ((i * 7) % 14),
  dur: 14 + ((i * 5) % 12),
  delay: -((i * 3.7) % 20),
  blue: i % 3 !== 0,
  ring: i % 4 === 0,
}));
const LINES = Array.from({ length: 6 }, (_, i) => ({
  top: 8 + i * 16,
  dur: 9 + i * 2.5,
  delay: -i * 2.2,
  blue: i % 2 === 0,
}));

const FloatingBg = () => (
  <div className="pointer-events-none absolute inset-0 overflow-hidden bg-black" aria-hidden="true">
    <div className="absolute left-1/2 top-1/3 h-72 w-72 -translate-x-1/2 rounded-full bg-blue-600/20 blur-[90px]" />
    {LINES.map((l, i) => (
      <span
        key={`l${i}`}
        className="ty-line absolute h-px w-2/3"
        style={{
          top: `${l.top}%`,
          animationDuration: `${l.dur}s`,
          animationDelay: `${l.delay}s`,
          background: `linear-gradient(90deg, transparent, ${l.blue ? "rgba(59,130,246,.7)" : "rgba(255,255,255,.5)"}, transparent)`,
        }}
      />
    ))}
    {DOTS.map((d, i) => (
      <span
        key={`d${i}`}
        className={`ty-dot absolute rounded-full ${
          d.ring
            ? d.blue ? "border border-blue-400/60" : "border border-white/40"
            : d.blue ? "bg-blue-500/60" : "bg-white/50"
        }`}
        style={{
          left: `${d.left}%`,
          width: d.ring ? d.size * 2.5 : d.size,
          height: d.ring ? d.size * 2.5 : d.size,
          animationDuration: `${d.dur}s`,
          animationDelay: `${d.delay}s`,
          boxShadow: d.ring ? "none" : d.blue ? "0 0 12px rgba(59,130,246,.8)" : "0 0 10px rgba(255,255,255,.6)",
        }}
      />
    ))}
  </div>
);

// Lluvia de confeti azul y blanco (una sola vez al abrir).
const CONFETTI = Array.from({ length: 46 }, (_, i) => ({
  left: (i * 37) % 100,
  delay: ((i * 0.13) % 1.4).toFixed(2),
  dur: (2.4 + ((i * 7) % 10) / 10).toFixed(2),
  rot: (i * 47) % 360,
  drift: ((i % 7) - 3) * 14,
  color: ["#2563EB", "#60A5FA", "#FFFFFF", "#93C5FD", "#1D4ED8"][i % 5],
  w: 6 + (i % 3) * 2,
  round: i % 4 === 0,
}));

const Confetti = () => (
  <div className="pointer-events-none absolute inset-0 z-20 overflow-hidden" aria-hidden="true" data-testid="confetti">
    {CONFETTI.map((c, i) => (
      <span
        key={i}
        className={`ty-confetti absolute top-0 ${c.round ? "rounded-full" : "rounded-[2px]"}`}
        style={{
          left: `${c.left}%`,
          width: c.w,
          height: c.round ? c.w : c.w * 1.6,
          background: c.color,
          animationDelay: `${c.delay}s`,
          animationDuration: `${c.dur}s`,
          "--rot": `${c.rot}deg`,
          "--drift": `${c.drift}px`,
        }}
      />
    ))}
  </div>
);

// Hoja para dejar reseña: se guarda directo en los comentarios del portafolio.
const ReviewSheet = ({ pid, onClose, onDone }) => {
  const [rating, setRating] = useState(5);
  const [name, setName] = useState("");
  const [text, setText] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const send = async () => {
    if (!name.trim() || !text.trim()) return setErr("Escribe tu nombre y tu comentario.");
    setBusy(true);
    setErr("");
    try {
      await apiAddReview(pid, "auto", { name, text, rating, source: "purchase" });
      onDone();
    } catch (e) {
      setErr("No se pudo enviar. Intenta de nuevo.");
    } finally {
      setBusy(false);
    }
  };
  return (
    <div className="fixed inset-0 z-50 flex items-end justify-center bg-black/70 backdrop-blur-sm sm:items-center" onClick={onClose}>
      <div
        className="w-full max-w-sm rounded-t-3xl border border-white/10 bg-[#0b1120] p-5 text-left sm:rounded-3xl"
        onClick={(e) => e.stopPropagation()}
        data-testid="review-sheet"
      >
        <div className="mb-3 flex items-center justify-between">
          <h2 className="font-display text-lg font-bold text-white">Califica tu compra</h2>
          <button onClick={onClose} className="rounded-full p-1.5 text-white/60 hover:text-white">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="flex justify-center py-2">
          <StarPicker value={rating} onChange={setRating} />
        </div>
        <input
          value={name}
          onChange={(e) => setName(e.target.value)}
          placeholder="Tu nombre"
          maxLength={60}
          className="mt-3 w-full rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-blue-500 focus:outline-none"
          data-testid="review-name"
        />
        <textarea
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder="Cuéntanos tu experiencia"
          rows={3}
          maxLength={500}
          className="mt-2 w-full resize-none rounded-xl border border-white/10 bg-white/5 px-3 py-2.5 text-sm text-white placeholder:text-white/35 focus:border-blue-500 focus:outline-none"
          data-testid="review-text"
        />
        {err && <p className="mt-1 text-xs text-red-400">{err}</p>}
        <button
          onClick={send}
          disabled={busy}
          className="mt-3 w-full rounded-xl bg-blue-600 py-3 text-sm font-bold text-white transition hover:bg-blue-500 disabled:opacity-60"
          data-testid="review-send"
        >
          {busy ? "Enviando…" : "Publicar reseña"}
        </button>
      </div>
    </div>
  );
};

// Número de WhatsApp: el configurado para "gracias" o el primer botón de WhatsApp del portafolio.
const findWhatsapp = (p) => {
  if (p.thanksWa) return p.thanksWa;
  const btn = (p.blocks || []).find((b) => b.type === "button" && b.variant === "whatsapp" && b.value);
  return btn ? btn.value : "";
};

export default function ThankYou() {
  const { slug, domain } = useParams();
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewed, setReviewed] = useState(false);
  const { data: p, notFound } = usePublicPortfolio(slug);
  const back = domain ? `/${domain}/${slug}` : `/p/${slug}`;
  const num = p ? findWhatsapp(p).replace(/\D/g, "") : "";
  const name = p?.profile?.businessName || p?.profile?.name || "";
  const msg = p?.thanksMsg || `Hola${name ? ` ${name}` : ""}, acabo de realizar mi pago. ¿Me confirmas por favor?`;

  return (
    <div className="relative flex min-h-[100dvh] items-center justify-center overflow-hidden bg-black px-6 py-12 text-center" data-testid="thank-you-page">
      <FloatingBg />
      <Confetti />
      <div className="relative z-10 flex w-full max-w-sm flex-col items-center">
        <div className="relative flex h-24 w-24 items-center justify-center">
          <span className="ty-ring absolute inset-0 rounded-full bg-blue-400/40" />
          <span className="ty-check relative flex h-24 w-24 items-center justify-center rounded-full bg-blue-600 shadow-[0_12px_40px_rgba(37,99,235,.6)]">
            <Check className="h-12 w-12 text-white" strokeWidth={3} />
          </span>
        </div>
        <h1 className="mt-7 font-display text-3xl font-bold text-white sm:text-4xl" data-testid="thank-you-title">
          ¡Gracias por tu compra!
        </h1>
        <p className="mt-3 text-sm leading-relaxed text-white/70">
          Tu pago fue recibido correctamente.
          {name ? ` ${name} te contactará muy pronto.` : ""}
        </p>
        {p?.profile?.avatar && p.profile.avatarType !== "video" && !/\.(mp4|webm|mov)/i.test(p.profile.avatar) && (
          <img src={p.profile.avatar} alt={name} className="mt-6 h-16 w-16 rounded-full border-2 border-white/30 object-cover" />
        )}
        {num && (
          <a
            href={`https://wa.me/${num}?text=${encodeURIComponent(msg)}`}
            target="_blank"
            rel="noopener noreferrer"
            className="mt-7 flex w-full items-center justify-center gap-2 rounded-2xl bg-[#25D366] py-3.5 text-base font-bold text-white shadow-[0_12px_30px_rgba(37,211,102,.4)] transition hover:brightness-110 active:scale-95"
            data-testid="thank-you-whatsapp"
          >
            <FaWhatsapp className="h-5 w-5" /> Confirmar por WhatsApp
          </a>
        )}
        {p && !reviewed && (
          <button
            onClick={() => setReviewOpen(true)}
            className="mt-3 flex w-full items-center justify-center gap-2 rounded-2xl border border-white/20 bg-white/5 py-3.5 text-base font-bold text-white backdrop-blur transition hover:bg-white/10 active:scale-95"
            data-testid="thank-you-review-btn"
          >
            <Star className="h-5 w-5 text-amber-400" fill="#FBBF24" /> Dejar mi reseña
          </button>
        )}
        {reviewed && (
          <p className="mt-4 rounded-xl bg-blue-500/15 px-4 py-3 text-sm font-semibold text-blue-100" data-testid="thank-you-reviewed">
            ¡Gracias por tu reseña! Ya aparece en los comentarios.
          </p>
        )}
        {!notFound && (
          <Link
            to={back}
            className="mt-4 inline-flex items-center gap-1.5 text-sm font-semibold text-white/70 transition hover:text-white"
            data-testid="thank-you-back"
          >
            <ArrowLeft className="h-4 w-4" /> Volver {name ? `a ${name}` : "al portafolio"}
          </Link>
        )}
      </div>
      {reviewOpen && p && (
        <ReviewSheet
          pid={p.id}
          onClose={() => setReviewOpen(false)}
          onDone={() => {
            setReviewOpen(false);
            setReviewed(true);
            localStorage.removeItem(`pf-cache:${slug}`);
          }}
        />
      )}
    </div>
  );
}
